import React, { forwardRef } from 'react'
import { Button } from 'antd'
import { ClockCircleOutlined } from '@ant-design/icons'
import {
  useReturnEligibility,
  calculateReturnDaysRemaining,
  getReturnWindowUrgency,
  getReturnWindowUrgencyColor,
  ReturnWindowUrgency,
} from '../../hooks/useReturnEligibility'
import { useTracking } from '../../hooks/useTracking'
import { useAnalytics } from '../../hooks/useAnalytics'
import DetailCard from '../common/detail-card'
import ReturnStatusDisplay from './return-status-display'
import styled from 'styled-components'
import theme from '../../theme'

const Content = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 10px;
  font-weight: 100;
  font-size: 16px;
  line-height: 21px;
  color: ${theme.midnight800};
`

const WindowRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  font-size: 13px;
  line-height: 20px;
  color: ${theme.midnight800};
`

const WindowIcon = styled(ClockCircleOutlined)<{ $urgency: ReturnWindowUrgency }>`
  color: ${({ $urgency }) => getReturnWindowUrgencyColor($urgency)};
  font-size: 14px;
`

type Props = {
  onRequestReturn?: () => void
  returnRequestId?: string
}

function ReturnRequestCard(
  { onRequestReturn, returnRequestId }: Props,
  ref: React.Ref<HTMLDivElement>
) {
  const {
    eligible,
    hasExistingReturn,
    existingReturnTrackingUrl,
    returnWindowClosesAt,
    accountId,
    isLoading,
  } = useReturnEligibility()
  const { orderDetails } = useTracking()
  const { track } = useAnalytics()

  if (isLoading || (!eligible && !hasExistingReturn)) {
    return null
  }

  const handleRequestReturn = () => {
    track('returns.request.started', {
      action: 'Click',
      category: 'Returns',
      product: 'TrackOrder',
      orderId: String(orderDetails?.id),
      accountId,
    })
    onRequestReturn?.()
  }

  const daysRemaining = calculateReturnDaysRemaining(returnWindowClosesAt)
  const showCountdown = eligible && daysRemaining !== null && daysRemaining > 0
  const urgency = showCountdown
    ? getReturnWindowUrgency(daysRemaining!)
    : 'normal'

  return (
    <DetailCard ref={ref} title="Return" data-testid="return-request-card">
      <Content>
        {eligible ? (
          <Button
            type="primary"
            onClick={handleRequestReturn}
            data-testid="request-return-button"
          >
            Request Return
          </Button>
        ) : hasExistingReturn ? (
          <ReturnStatusDisplay
            returnRequestId={returnRequestId}
            existingReturnTrackingUrl={existingReturnTrackingUrl}
          />
        ) : null}
        {showCountdown && (
          <WindowRow
            data-testid="return-window-remaining"
            data-urgency={urgency}
          >
            <WindowIcon $urgency={urgency} />
            <span>
              You have {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} left
              to request a return
            </span>
          </WindowRow>
        )}
      </Content>
    </DetailCard>
  )
}

export default forwardRef(ReturnRequestCard)
